import { AfterViewInit, Component, ElementRef, Input, NgZone } from '@angular/core';
import { ChatMessage, User } from "@banta/common";
import { ChatSource, HashTag, MessageMenuItem } from "@banta/sdk";
import { Shell, AppConfig } from "@tytapp/common";
import { environment } from "@tytapp/environment";
import { ReportComponent } from "../../moderation";
import { LoginComponent, UserService } from "@tytapp/user";
import { TYTBantaAuthentication } from "../tyt-banta-authentication";
import { ActivatedRoute, Router } from '@angular/router';
import { ApiUser, CommentsApi } from '@tytapp/api';
import { ShareCommentComponent } from '../share-comment/share-comment.component';
import { ImpressionsApi } from '@tytapp/api';
import { UserAuthMediatorComponent } from '@tytapp/user/user-auth-mediator/user-auth-mediator.component';
import { Subscription } from 'rxjs';


@Component({
    selector: 'tyt-comments',
    styleUrls: ['./comments.component.scss'],
    templateUrl: './comments.component.html'
})
export class TYTCommentsComponent implements AfterViewInit {
    constructor(
        private tytBantaAuth: TYTBantaAuthentication,
        private shell: Shell,
        private appConfig: AppConfig,
        private userService: UserService,
        private router: Router,
        private commentService: CommentsApi,
        private elementRef: ElementRef,
        private ngZone: NgZone,
        private impressionsApi: ImpressionsApi,
        private activatedRoute: ActivatedRoute
    ) {
    }

    customMenuItems: MessageMenuItem[] = [];

    @Input()
    identifier: string;

    @Input()
    requirementType: 'membership' | 'registration' = 'membership';

    @Input()
    shareTitle: string;

    @Input()
    scrollToLoad = true;

    readyToLoad = false;
    hashtags: HashTag[] = [];
    learnMore = false;

    private legacyOverride = false;
    private bantaEnabled = false;
    get useBanta() {
        return this.bantaEnabled && !this.legacyOverride;
    }

    switchBack() {
        this.legacyOverride = true;
    }

    showSignIn() {
        this.userService.source = `${this.identifier}_comments`;
        return this.shell.showDialog(UserAuthMediatorComponent);
    }

    editAvatar() {
        window.open(`/settings/profile`, '_blank');
    }

    async showPermissionDenied(reason: string) {
        await this.userService.ready;

        if (reason === 'membership-required') {
            if (this.userService.user.entitled) {
                alert('You cannot comment on this content with your current plan');
                window.open(`${environment.urls.accounts}/membership/change`, '_blank');
            } else {
                alert('You must be a member to comment on this content');
                window.open(environment.urls.purchase, '_blank');
            }
        } else if (reason === 'username-required') {
            const currentRoute = this.router.routerState.snapshot.url;
            this.router.navigateByUrl(`/settings/account/change-username?return=${currentRoute}`);
        } else if (reason === 'vote-required') {
            if (this.userService.user.entitled)
                alert('You must vote in this poll before commenting.')
            else
                alert('You must vote in this poll or become a member before commenting.')
            return;
        } else if (reason === 'signature-required') {
            if (this.userService.user.entitled)
                alert('You must sign this petition before commenting.');
            else
                alert('You must sign this petition or become a member before commenting.');
            return;
        } else {
            // Should not happen
            alert(`You cannot currently comment on this topic (${reason})`);
        }
    }

    async reportMessage(message: ChatMessage) {
        await this.userService.ready;
        if (!this.userService.user) {
            this.shell.showDialog(LoginComponent, "Sign in to report this content");
        } else {
            this.shell.showDialog(ReportComponent, message);
        }
    }

    navigateToPublicProfile(user: User) {
        console.log(user);
        window.open(`/community/profiles/${user.username}`, '_blank');
    }

    shareComment(message: ChatMessage) {
        const shareButtonElement = document.querySelectorAll(`[data-comment-id="${message.id}"]`)
            .item(0)
            .querySelector('.message-content')
            .querySelector('.counted-action:last-of-type');

        if (shareButtonElement) {

            this.shell.showDialog(ShareCommentComponent, message);
        }
    }

    isAPrivateReply = false;

    setPrivateReplyMode() {
        this.isAPrivateReply = true;
    }

    setPublicReplyMode() {
        this.isAPrivateReply = false;
    }

    showPrivateReplyInfo() {
        alert(
            `The private reply feature allows moderators `
            + ` to email a response to a comment instead of post it publically.`
        );
    }

    showPublicReplyInfo() {
        alert(
            `Your reply will be sent as a normal public comment reply that all users can see.`
        );
    }

    async interceptReplySend(chatMessage: ChatMessage, source: ChatSource) {
        if (!this.isAPrivateReply)
            return false;

        this.sendPrivateReply(chatMessage, source);
        return true;
    }

    async sendPrivateReply(chatMessage: ChatMessage, source: ChatSource): Promise<void> {
        try {
            const result = await this.commentService.createPrivateReply(source.parentIdentifier, {
                parent_comment_uuid: source.parentIdentifier,
                message: chatMessage.message
            }).toPromise();
            alert("Your private reply has been sent to the user's email address");
        } catch (error) {
            if (error.json)
                error = error.json();

            let message = error.message || error.error;

            switch (message) {
                case 'not-found':
                    alert('Cannot send a private reply to this comment as it has not been synchronized to TYT Platform yet. Please try again later.')
                    break;
                case 'unauthorized':
                    alert('Cannot send a private reply: Please reload TYT.com and try again.');
                    break;
                case 'forbidden':
                    alert('Cannot send a private reply: You do not have permission to perform this action.')
                    break;
            }
            console.error(error);
        }

        this.isAPrivateReply = false;
    }

    get canPrivateReply() {
        return this.userService.user?.staff || this.userService.user?.trust_level > 1;
    }

    private recordedImpression = false;
    isClientSide = typeof window !== undefined;

    async recordImpressions() {
        const impressionsEnabled = await this.shell.hasFeature('apps.web.impressions.record_for_comments');
        if (!impressionsEnabled) return;

        this.ngZone.runOutsideAngular(() => {
            new IntersectionObserver(async ([entry], observer) => {
                if (entry.isIntersecting) {
                    observer.unobserve(this.elementRef.nativeElement);
                    if (!this.recordedImpression) {
                        await this.impressionsApi.makeImpression(btoa(JSON.stringify({type: 'comments', id: this.identifier})));
                        this.recordedImpression = true;
                    }
                }
            }).observe(this.elementRef.nativeElement);
        });
    }

    private _subs = new Subscription();
    user: ApiUser;

    get isMember() {
        if (!this.user)
            return false;

        return this.user.entitled;
    }

    allowSwitchBack = false;

    async ngOnInit() {
        this._subs.add(this.userService.userChanged.subscribe(user => {
            this.user = user;
            if (this.user && (this.user.trust_level > 0 || this.user.staff)) {
                this.customMenuItems = [
                    {
                        icon: 'traffic',
                        label: 'Moderation',
                        action: (message: ChatMessage) => {
                            window.open(`${environment.urls.platform}/admin/comments/${message.id}`, '_blank')
                        }
                    }
                ];
            } else {
                this.customMenuItems = [
                    // None
                ];
            }
        }));
        this.bantaEnabled = await this.appConfig.featureEnabled("apps.web.use_banta");
        this.allowSwitchBack = await this.appConfig.featureEnabled("apps.web.allow_spotim_switchback");

        if (typeof window !== 'undefined') {
            if (localStorage['tyt:bantaEnabled'])
                this.bantaEnabled = localStorage['tyt:bantaEnabled'] === '1';
            if (localStorage['tyt:bantaEnabled:allowSwitchBack'])
                this.allowSwitchBack = localStorage['tyt:bantaEnabled:allowSwitchBack'] === '1';
        }

        this.readyToLoad = true;
    }

    ngOnDestroy() {
        this._subs.unsubscribe();
    }

    ngAfterViewInit() {
        this.recordImpressions();
    }

    get postReplyLabel() {
        if (this.isAPrivateReply)
            return `Reply privately via email`;
        else
            return `Post a reply`;
    }
}
